/* eslint-disable unicorn/no-thenable */
import colors from '../../../../colors';
import { THRESHOLD_TYPES } from '../../../../constants';

const thresholdOptions = {
  [THRESHOLD_TYPES.linear.value]: {
    value: {
      type: 'integer',
      title: 'Value',
      description: 'Value where the threshold should end.',
      default: 100,
    },
  },
  [THRESHOLD_TYPES.area.value]: {
    min: {
      type: 'integer',
      title: 'Min value',
      description: 'Value where the threshold should start.',
      default: 1,
    },
    max: {
      type: 'integer',
      title: 'Max value',
      description: 'Value where the threshold should end.',
      default: 50,
    },
  },
};

/**
 * Valid trigger conditions for each threshold type:
 * - linear: '>', '>=', '<', '<=' (all conditions except "between" map to reference lines)
 * - area: '>', '>=', '<', '<=', '<>' (range-based conditions map to reference areas)
 */
const LINEAR_VALID_CONDITIONS = ['>', '>=', '<', '<=', '='];
const AREA_VALID_CONDITIONS = [...LINEAR_VALID_CONDITIONS, '<>'];

/**
 * Checks if a stateset has valid trigger conditions for the given threshold type
 * @param {Object} stateset - The stateset object
 * @param {string} thresholdType - 'linear' or 'area'
 * @returns {boolean}
 */
export const hasValidStatesetForThreshold = (stateset, thresholdType) => {
  if (!stateset || stateset.isVirtualStateSet) return false;

  const states = Array.isArray(stateset.states) ? stateset.states : [];
  const validConditions =
    thresholdType === THRESHOLD_TYPES.linear.value
      ? LINEAR_VALID_CONDITIONS
      : AREA_VALID_CONDITIONS;

  return states.some(
    (state) => state.trigger?.condition && validConditions.includes(state.trigger.condition)
  );
};

/**
 * Filters variables to only those with valid statesets for the given threshold type
 * @param {Array} variables - Array of visualization variables
 * @param {string} thresholdType - 'linear' or 'area'
 * @returns {Array}
 */
export const getVariablesWithValidStatesets = (variables, thresholdType) => {
  return (variables || []).filter((variable) =>
    hasValidStatesetForThreshold(variable.stateset, thresholdType)
  );
};

/**
 * Converts stateset states to threshold items based on threshold type
 * @param {Object} stateset - The stateset object
 * @param {string} thresholdType - 'linear' or 'area'
 * @param {Object} options - Additional options (dataMin, dataMax for area type bounds)
 * @returns {Array} Array of threshold items in the expected format
 */
export const convertStatesetToThresholds = (stateset, thresholdType, options = {}) => {
  if (!stateset || stateset.isVirtualStateSet) return [];

  const states = Array.isArray(stateset.states) ? stateset.states : [];
  const { dataMin = 0, dataMax = 100 } = options;

  // Calculate 10% padding for unbounded ranges
  const range = dataMax - dataMin;
  const paddedMin = dataMin - range * 0.1;
  const paddedMax = dataMax + range * 0.1;

  if (thresholdType === THRESHOLD_TYPES.linear.value) {
    // For linear: use all conditions except "between (<>)", which doesn't fit linear thresholds
    return states
      .filter((state) => LINEAR_VALID_CONDITIONS.includes(state.trigger?.condition))
      .map((state) => ({
        fill: state.color,
        value: state.trigger.triggerValue,
        description: state.description || state.name,
      }))
      .toSorted((a, b) => a.value - b.value);
  }

  if (thresholdType === THRESHOLD_TYPES.area.value) {
    const thresholds = [];

    // Collect all states with valid conditions and sort by trigger value
    const validStates = states
      .filter((state) => AREA_VALID_CONDITIONS.includes(state.trigger?.condition))
      .map((state) => ({
        ...state,
        sortValue:
          state.trigger.condition === '<>'
            ? state.trigger.triggerValue1
            : state.trigger.triggerValue,
      }))
      .toSorted((a, b) => (a.sortValue ?? 0) - (b.sortValue ?? 0));

    for (const state of validStates) {
      const { trigger, color, description, name } = state;
      const { condition, triggerValue, triggerValue1, triggerValue2 } = trigger;

      switch (condition) {
        case '<>': {
          // Between: direct mapping to area
          thresholds.push({
            fill: color,
            min: triggerValue1,
            max: triggerValue2,
            description: description || name,
          });
          break;
        }
        case '>':
        case '>=': {
          // Greater than: find next threshold or use padded max
          const currentValue = triggerValue;
          const nextState = validStates.find(
            (s) =>
              s !== state &&
              (s.sortValue ?? 0) > currentValue &&
              (s.trigger.condition === '>' ||
                s.trigger.condition === '>=' ||
                s.trigger.condition === '<>')
          );
          const upperBound = nextState ? nextState.sortValue : paddedMax;

          thresholds.push({
            fill: color,
            min: currentValue,
            max: upperBound,
            description: description || name,
          });
          break;
        }
        case '<':
        case '<=': {
          // Less than: find next threshold or use padded min
          const currentValue = triggerValue;
          const nextState = validStates.find(
            (s) =>
              s !== state &&
              (s.sortValue ?? 0) < currentValue &&
              (s.trigger.condition === '<' ||
                s.trigger.condition === '<=' ||
                s.trigger.condition === '<>')
          );
          const lowerBound = nextState ? nextState.sortValue : paddedMin;

          thresholds.push({
            fill: color,
            min: lowerBound,
            max: currentValue,
            description: description || name,
          });
          break;
        }
        case '=': {
          // Equal: exact value maps to a point area
          thresholds.push({
            fill: color,
            min: triggerValue,
            max: triggerValue,
            description: description || name,
          });
          break;
        }
        default: {
          break;
        }
      }
    }

    return thresholds.toSorted((a, b) => a.min - b.min);
  }

  return [];
};

/**
 * Creates the threshold schema with optional signal-based threshold support
 * @param {Array} variables - Optional array of visualization variables with statesets
 * @returns {Object} The threshold schema object
 */
const getThresholdSchema = (variables = []) => {
  // Build signal options for each threshold type
  const getSignalOptionsForType = (thresholdType) => {
    const validVariables = getVariablesWithValidStatesets(variables, thresholdType);

    if (validVariables.length === 0) {
      return {
        type: 'string',
        title: 'Signal',
        default: '',
        oneOf: [{ enum: [''], title: `No signals with stateset found` }],
      };
    }

    return {
      type: 'string',
      title: 'Signal',
      default: validVariables[0]?.id || '',
      oneOf: validVariables.map((variable) => ({
        enum: [variable.id],
        title: `${variable.stateset?.name ?? ''} [ ${variable.sourceName ?? '-'} ] [ ${variable.itemDesignation ?? '-'} ] ${variable.name || variable.id}`,
      })),
    };
  };

  return {
    thresholds: {
      title: 'Thresholds',
      type: 'object',
      properties: {
        thresholdType: {
          type: 'string',
          title: 'Threshold type',
          default: THRESHOLD_TYPES.linear.value,
          oneOf: Object.values(THRESHOLD_TYPES).map((item) => ({
            enum: [item.value],
            title: item.name,
          })),
        },
        useSignalThreshold: {
          type: 'boolean',
          title: 'Use signal threshold',
          default: false,
        },
      },
      dependencies: {
        useSignalThreshold: {
          oneOf: [
            {
              // When useSignalThreshold is false, show manual threshold config
              properties: {
                useSignalThreshold: { enum: [false] },
              },
            },
            {
              // When useSignalThreshold is true, show signal selector based on threshold type
              properties: {
                useSignalThreshold: { enum: [true] },
              },
            },
          ],
        },
        thresholdType: {
          oneOf: Object.values(THRESHOLD_TYPES).map(({ key, value: type }) => ({
            properties: {
              thresholdType: { enum: [type] },
            },
            // Use allOf to handle both useSignalThreshold states
            allOf: [
              {
                if: {
                  properties: {
                    useSignalThreshold: { enum: [false] },
                  },
                },
                then: {
                  properties: {
                    [key]: {
                      maxItems: 10,
                      type: 'array',
                      title: '',
                      default: [],
                      items: {
                        type: 'object',
                        properties: {
                          fill: {
                            type: 'string',
                            title: 'Fill',
                            default: colors[0].id,
                            oneOf: colors.map((color) => ({
                              enum: [color.id],
                              title: color.label,
                            })),
                          },
                          description: {
                            type: 'string',
                            title: 'Description',
                            description: 'Description for the threshold.',
                            default: '',
                          },
                          ...thresholdOptions[type],
                        },
                      },
                    },
                  },
                },
              },
              {
                if: {
                  properties: {
                    useSignalThreshold: { enum: [true] },
                  },
                },
                then: {
                  properties: {
                    signalId: getSignalOptionsForType(type),
                  },
                },
              },
            ],
          })),
        },
      },
    },
  };
};

/**
 * Resolves threshold items from configuration, supporting both manual and signal-based thresholds.
 * @param {Object} thresholds - The thresholds configuration object
 * @param {Array} variables - The visualization's variables array (with stateset info)
 * @param {Object} options - Optional { dataMin, dataMax } for area threshold bounds
 * @returns {Array} Sorted array of threshold items
 */
export const resolveThresholdItems = (thresholds = {}, variables = [], options = {}) => {
  const { thresholdType } = thresholds;
  const currentThresholdKey = THRESHOLD_TYPES[thresholdType]?.key || '';

  // Signal-based thresholds
  if (thresholds.useSignalThreshold && thresholds.signalId) {
    const selectedVariable = variables.find((v) => v.id === thresholds.signalId);
    const stateset = selectedVariable?.stateset;

    if (stateset && !stateset.isVirtualStateSet) {
      const { dataMin = 0, dataMax = 100 } = options;
      return convertStatesetToThresholds(stateset, thresholdType, { dataMin, dataMax });
    }
    return [];
  }

  // Manual thresholds
  const items = thresholds[currentThresholdKey];
  if (!Array.isArray(items)) return [];

  return items.toSorted((a, b) =>
    thresholdType === THRESHOLD_TYPES.linear.value ? a.value - b.value : a.min - b.min
  );
};

// Export the function for dynamic schema generation
export { getThresholdSchema };
